import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider } from "@/context/SiteContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingShop from "@/components/FloatingShop";
import Home from "@/pages/Home";
import FreeHub from "@/pages/FreeHub";
import Library from "@/pages/Library";
import Resource from "@/pages/Resource";
import Community from "@/pages/Community";
import Membership from "@/pages/Membership";
import { Login, Register, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import { CustomPage, Unsubscribe } from "@/pages/Misc";
import Admin from "@/pages/admin/Admin";
import Weekly from "@/pages/Weekly";
import Challenge from "@/pages/Challenge";
import Certificate from "@/pages/Certificate";
import Bedtime from "@/pages/Bedtime";
import Playlist from "@/pages/Playlist";
import { RemindersOff } from "@/pages/Misc";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-[60vh]" />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/account" replace />;
  return children;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="flex min-h-screen flex-col">
      <ScrollTop />
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/free" element={<FreeHub />} />
          <Route path="/library" element={<Library />} />
          <Route path="/r/:slug" element={<Resource />} />
          <Route path="/community" element={<Community />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account" element={<Protected><Dashboard /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/challenge/:slug" element={<Challenge />} />
          <Route path="/challenge/:slug/certificate" element={<Protected><Certificate /></Protected>} />
          <Route path="/reminders-off" element={<RemindersOff />} />
          <Route path="/daily-off" element={<RemindersOff daily />} />
          <Route path="/bedtime" element={<Bedtime />} />
          <Route path="/playlist/:slug" element={<Playlist />} />
          <Route path="/weekly" element={<Weekly />} />
          <Route path="/p/:slug" element={<CustomPage />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="*" element={<CustomPage notFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <FloatingShop />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <AuthProvider>
          <Shell />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}
